import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import AppNavBar from './layout/AppNavBar';
import BottomTabBar from './layout/BottomTabBar';
import mammoth from 'mammoth';
import { downloadResume } from '../utils/docxGenerator';
import { autoSaveDocument } from '../services/autoSaveApi';
import { generateDocument, scoreOriginalResume, fetchCredits, createCheckoutSession } from '../services/documentApi';
import { setCredits, setIsAdmin } from '../store/authSlice';
import { saveResume, getSavedResume, getSavedResumes, saveNamedResume } from '../services/resumeApi';
import DocumentSelector from './inputs/DocumentSelector';
import ResumeInput from './inputs/ResumeInput';
import ApplicationSource from './inputs/ApplicationSource';
import ApplicationInfo from './inputs/ApplicationInfo';
import GenerateButton from './ui/GenerateButton';
import ResultsPage from './results/ResultsPage';
import ConfirmModal from './shared/ConfirmModal';
import { DEMO_RESUME, DEMO_JOB_DESCRIPTION, DEMO_APPLICATION_INFO } from '../constants/demoData';
import { getDraftKey, writeLocalDraft, readLocalDraft, clearLocalDraft } from '../utils/applicationDraft';
import { resetApplicationState, isApplicationDirty } from '../utils/resetApplicationState';



const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';


pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";


// pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
const SKU_OPTIONS = [
  { sku: 'EMP-APP-1',    label: '1 Credit',           price: '$3.99'  },
  { sku: 'EMP-APP-3',    label: '3 Credits',           price: '$9.99'  },
  { sku: 'EMP-APP-10',   label: '10 Credits',          price: '$29.99' },
  { sku: 'EMP-ADDON-10', label: '10 Credits (Add-on)', price: '$24.99' },
];

// Maps one successful NDJSON "result" event into the shape the results UI renders.
// Pulled out of handleOptimize so the streaming path (one call per document as it
// arrives) and a single call site both run the exact same parsing/cleaning —
// preview and download always read the same cleaned string.
function mapGeneratedResult(r, originalScores) {
  let parsed = {};
  if (r.optimizedContent) {
    try {
      parsed = JSON.parse(r.optimizedContent);
    } catch (e) {
      console.error("Failed to parse optimizedContent", e);
    }
  }

  // Score extraction — reads real LLM scores only, no random fallback.
  // Schema structure varies by doc type:
  //   resume/cv/bio/coverletter: parsed.scores.ats_composite.overall_score
  //   linkedin:                 parsed.composite_result.ats_score_percent
  //   portfolio:                parsed.final_composite.ats_score
  const scores = parsed.scores || {};
  const atsComposite = scores.ats_composite || {};
  const humanComposite = scores.human_composite || {};

  const atsOverall =
    atsComposite.overall_score ??
    parsed.composite_result?.ats_score_percent ??
    parsed.final_composite?.ats_score ??
    null;

  const humanOverall =
    humanComposite.overall_score ??
    parsed.composite_result?.human_score_percent ??
    parsed.final_composite?.human_score ??
    null;

  const atsCategories = atsComposite.categories || [];
  const humanCategories = humanComposite.categories || [];

  // Safety net: strips a trailing JSON blob if the model leaks schema
  // fields into resume_text. Should not fire after prompt fix (2A) but kept as guard.
  const resumeText = (parsed.resume_text || "")
    .replace(/\{[\s\S]*?"(?:ATS|TruthMap|ats_composite|human_composite|scores)"[\s\S]*$/, "")
    .trim();

  return {
    rawOptimizedContent: r.optimizedContent,
    description: parsed.description || "",
    resumeText,
    scores: parsed.scores || {},
    atsComposite,
    humanComposite,
    atsOverall,
    atsCategories,
    humanOverall,
    humanCategories,
    keywordMatches: parsed.keyword_matches || [],
    fixPanel: r.fixPanel || [],
    originalResumeText: resumeText,
    originalScores,
    docType: r.docType,
    metadata: r.metadata,
  };
}

const Layout = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.auth.session);
  const credits = useSelector((state) => state.auth.credits);
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const location = useLocation();
  const navigate = useNavigate();

  // { [docType]: 'saving' | 'saved' | 'error' }
  const [saveStates, setSaveStates] = useState({});

  const [resume, setResume] = useState('');
  const [fileName, setFileName] = useState(null);
  const [resumePreloaded, setResumePreloaded] = useState(false);
  const [resumeSavedAt, setResumeSavedAt] = useState(null);
  const [savedResumes, setSavedResumes] = useState([]);
  const [jobDescription, setJobDescription] = useState('');
  const [language, setLanguage] = useState('English (US)');
  const [generatedLanguage, setGeneratedLanguage] = useState(null);
  const [tone, setTone] = useState('Professional');
  const [applicationInfo, setApplicationInfo] = useState({
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    company: '',
    targetJobTitle: '',
    hiringManager: '',
    title: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [resultsView, setResultsView] = useState(false);
  const [result, setResult] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  // From the stream's summary/error line: true only when the backend confirms the
  // run's credit was returned. null/false means "not confirmed" (old backend, dropped
  // connection, regrant failed) and the results view falls back to contact-support copy.
  const [creditRegranted, setCreditRegranted] = useState(null);
  // The stream broke before any document arrived — there is nothing to show but the
  // failed panel.
  const [generationFailed, setGenerationFailed] = useState(false);
  const [applicationSource, setApplicationSource] = useState({
    jobDescription: { selected: false, text: '' },
    service: { selected: false, text: '' },
    industry: { selected: false, text: '' },
    role: { selected: false, text: '' },
    company: { selected: false, text: '' },
    other: { selected: false, text: '' }
  });
  const [documents, setDocuments] = useState([
    { type: 'resume', selected: false, style: 'Professional / Corporate', audience: 'ATS / Human' },
    { type: 'cv', selected: false, style: 'Professional / Corporate', audience: 'ATS / Human' },
    { type: 'coverLetter', selected: false, style: 'Professional / Corporate', audience: 'ATS / Human' },
    { type: 'linkedin', selected: false, style: 'Professional & Polished', audience: 'ATS / Human' },
    { type: 'bio', selected: false, style: 'Professional & Polished', audience: 'ATS / Human' },
    { type: 'portfolio', selected: false, style: 'Bold & Creative', audience: 'ATS / Human' }
  ]);

  // FixPanel State
  const [fixStates, setFixStates] = useState({});
  const [loadingDocs, setLoadingDocs] = useState({}); // { resume: true, cv: false, ... }

  // Original resume scores — captured once before generation, shared across all doc results
  const originalScoresRef = useRef(null);
  // Stores latest freeform edit text per result index — avoids stale-state read in handleAcceptFix
  const latestFreeformRef = useRef({});
  // Guards against double-loading saved resume across re-renders
  const hasLoadedResumeRef = useRef(false);
  // AbortController for the in-flight generation stream — aborted on unmount or "New Generation"
  const streamAbortControllerRef = useRef(null);

  // Cancel an in-flight generation stream if the user navigates away mid-request
  useEffect(() => {
    return () => streamAbortControllerRef.current?.abort();
  }, []);

  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showNewAppConfirm, setShowNewAppConfirm] = useState(false);
  const [showDraftRestoredNotice, setShowDraftRestoredNotice] = useState(false);
  const [buyingCredits, setBuyingCredits] = useState(false);
  // 'success' | 'cancelled' | null — set when Stripe redirects back
  const [checkoutStatus, setCheckoutStatus] = useState(null);

  // Load a saved document from the dashboard (passed via router state)
  useEffect(() => {
    if (location.state?.loadedResults) {
      setResults(location.state.loadedResults);
      setResultsView(true);
      if (location.state.applicationInfo) {
        setApplicationInfo(prev => ({ ...prev, ...location.state.applicationInfo }));
      }
      // Clear router state so a refresh doesn't reload it
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line

  // Restore form state on mount — the sessionStorage bridge (saved right before a credits
  // redirect) takes precedence over the localStorage autosave draft, since it's more complete
  // (includes the resume) and more recent. Only fall back to the localStorage draft when the
  // bridge has nothing valid for this user.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;

    const draftKey = getDraftKey(userId);
    const saved = sessionStorage.getItem(draftKey);
    let sessionBridgeRestored = false;
    if (saved) {
      try {
        const draft = JSON.parse(saved);
        if (draft.userId === userId) {
          if (draft.resume)          setResume(draft.resume);
          if (draft.jobDescription)  setJobDescription(draft.jobDescription);
          if (draft.applicationInfo) setApplicationInfo(prev => ({ ...prev, ...draft.applicationInfo }));
          if (draft.applicationSource) setApplicationSource(draft.applicationSource);
          if (draft.documents)       setDocuments(draft.documents);
          sessionBridgeRestored = true;
        }
      } catch (_) {}
      sessionStorage.removeItem(draftKey);
    }

    if (sessionBridgeRestored) return;

    const localDraft = readLocalDraft(userId);
    if (localDraft) {
      if (localDraft.jobDescription)    setJobDescription(localDraft.jobDescription);
      if (localDraft.applicationInfo)   setApplicationInfo(prev => ({ ...prev, ...localDraft.applicationInfo }));
      if (localDraft.applicationSource) setApplicationSource(localDraft.applicationSource);
      if (localDraft.documents)         setDocuments(localDraft.documents);
      setShowDraftRestoredNotice(true);
    }
  }, []); // eslint-disable-line

  // Debounced localStorage autosave — persists everything except the resume (see Phase 13 design
  // note in docs/ANALYSIS.md). Skips the write entirely when there's nothing worth saving.
  const draftWriteTimerRef = useRef(null);
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return undefined;
    if (draftWriteTimerRef.current) clearTimeout(draftWriteTimerRef.current);
    draftWriteTimerRef.current = setTimeout(() => {
      if (isApplicationDirty({ resume, fileName, jobDescription, applicationInfo, applicationSource, documents })) {
        writeLocalDraft(userId, { jobDescription, applicationInfo, applicationSource, documents });
      }
    }, 1000);
    return () => clearTimeout(draftWriteTimerRef.current);
  }, [session?.user?.id, jobDescription, applicationInfo, applicationSource, documents]); // eslint-disable-line


  // Pre-populate resume and load saved resumes library on session load (runs once per session)
  useEffect(() => {
    if (!session?.access_token || hasLoadedResumeRef.current) return;
    hasLoadedResumeRef.current = true;
    getSavedResume(session.access_token).then(data => {
      if (data?.content) {
        setResume(data.content);
        setFileName(data.file_name || null);
        setResumePreloaded(true);
        setResumeSavedAt(data.updated_at || null);
      }
    }).catch(() => {});
    getSavedResumes(session.access_token).then(list => {
      setSavedResumes(list);
    }).catch(() => {});
  }, [session?.access_token]); // eslint-disable-line

  // Load credit count whenever the session changes
  useEffect(() => {
    if (!session?.access_token) return;
    fetchCredits(session.access_token).then(data => {
      if (data !== null) {
        dispatch(setCredits(data.credits));
        dispatch(setIsAdmin(data.isAdmin));
      }
    });
  }, [session?.access_token]); // eslint-disable-line

  // Handle Stripe redirect — ?checkout=success or ?checkout=cancelled
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (!checkout) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (checkout === 'success') {
      setCheckoutStatus('success');
      setShowUpgrade(false);
      if (session?.access_token) {
        fetchCredits(session.access_token).then(data => {
          if (data !== null) {
            dispatch(setCredits(data.credits));
            dispatch(setIsAdmin(data.isAdmin));
          }
        });
      }
      setTimeout(() => setCheckoutStatus(null), 5000);
    } else if (checkout === 'cancelled') {
      setCheckoutStatus('cancelled');
      setTimeout(() => setCheckoutStatus(null), 3000);
    }
  }, [location.search]); // eslint-disable-line


  const handleBuyCredits = async (sku) => {
    if (buyingCredits) return;
    setBuyingCredits(true);
    try {
      const { url } = await createCheckoutSession(sku, session?.access_token);
      window.location.href = url;
    } catch (err) {
      console.error('[Stripe] checkout error:', err);
      alert('Failed to start checkout. Please try again.');
      setBuyingCredits(false);
    }
  };

  const handleRemoveFile = () => {
    setFileName(null);
    setResume('');
    setResumePreloaded(false);
    setResumeSavedAt(null);
  };

  const handleResumeTextChange = (text) => {
    setResume(text);
  };

  const handleApplyResume = (content) => {
    setResume(content);
    setFileName(null);
    setResumePreloaded(false);
    setResumeSavedAt(null);
  };

  const handleSaveNamedResume = async (content, name) => {
    await saveNamedResume(content, name, session?.access_token);
    const updated = await getSavedResumes(session?.access_token);
    setSavedResumes(updated);
  };

  const applyFixToContent = (currentContent, fix, fixText) => {
    let updatedContent = currentContent;
    let fixApplied = false;

    const stripMarkdownBoldLocal = (text) => text.replace(/\*\*(.*?)\*\*/g, '$1');

    if (fix.keyword || fix.original?.includes("Missing keyword")) {
      const keyword = fix.keyword || fix.original.replace("Missing keyword: ", "").trim();
      const section = fix.section || 'Skills';
      const lines = updatedContent.split('\n');
      let sectionIndex = -1;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.toLowerCase().includes(section.toLowerCase()) &&
          (line === line.toUpperCase() || line.match(/^#{1,3}\s+/) ||
            /^(SUMMARY|EXPERIENCE|EDUCATION|SKILLS|COMPETENCIES|CORE COMPETENCIES)/i.test(line))) {
          sectionIndex = i;
          break;
        }
      }

      if (sectionIndex === -1) {
        sectionIndex = lines.findIndex(line => line.toLowerCase().includes(section.toLowerCase()));
      }

      if (sectionIndex >= 0) {
        let sectionEnd = lines.length;
        for (let i = sectionIndex + 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line && (line === line.toUpperCase() || line.match(/^#{1,3}\s+/) ||
            /^(SUMMARY|EXPERIENCE|EDUCATION|SKILLS|COMPETENCIES|CORE COMPETENCIES|PROFESSIONAL EXPERIENCE)/i.test(line))) {
            sectionEnd = i;
            break;
          }
        }

        const sectionLines = lines.slice(sectionIndex, sectionEnd);
        const sectionContent = sectionLines.join('\n');
        const keywordExists = sectionContent.toLowerCase().includes(keyword.toLowerCase()) ||
          sectionContent.toLowerCase().includes(fixText.toLowerCase());

        if (!keywordExists) {
          let lastLineIndex = sectionEnd - 1;
          while (lastLineIndex > sectionIndex && !lines[lastLineIndex].trim()) {
            lastLineIndex--;
          }

          const lastLine = lines[lastLineIndex];
          if (fixText.length <= 30 && (lastLine.trim().endsWith(',') || lastLine.trim().match(/^[-•*]\s/))) {
            lines[lastLineIndex] = lastLine.trim() + ' ' + fixText;
          } else if (fixText.length <= 30 && lastLine.trim().length > 0) {
            lines[lastLineIndex] = lastLine.trim() + ', ' + fixText;
          } else {
            lines.splice(lastLineIndex + 1, 0, '- ' + fixText);
          }

          updatedContent = lines.join('\n');
          fixApplied = true;
        }
      } else {
        updatedContent = currentContent + '\n\n**' + section + '**\n- ' + fixText;
        fixApplied = true;
      }
    } else {
      if (fix.original) {
        const contentStripped = stripMarkdownBoldLocal(currentContent);
        const originalStripped = stripMarkdownBoldLocal(fix.original);

        const originalIndex = contentStripped.indexOf(originalStripped);
        if (originalIndex !== -1) {
          const before = currentContent.substring(0, originalIndex);
          const after = currentContent.substring(originalIndex + originalStripped.length);
          updatedContent = before + fixText + after;
          fixApplied = true;
        } else {
          const regex = new RegExp(originalStripped.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
          const match = contentStripped.match(regex);
          if (match) {
            const startIndex = match.index;
            const endIndex = startIndex + match[0].length;
            const before = currentContent.substring(0, startIndex);
            const after = currentContent.substring(endIndex);
            updatedContent = before + fixText + after;
            fixApplied = true;
          } else {
            updatedContent = currentContent + '\n\n' + fixText;
            fixApplied = true;
          }
        }
      } else {
        const section = fix.section || 'Experience';
        updatedContent = currentContent + `\n\n**${section}**\n- ${fixText}`;
        fixApplied = true;
      }
    }

    return fixApplied ? updatedContent : currentContent;
  };

  const handleManualRewrite = (resultIndex, fixIndex, value) => {
    if (fixIndex === 'freeform') latestFreeformRef.current[resultIndex] = value;
    setFixStates(prev => ({
      ...prev,
      [resultIndex]: {
        ...prev[resultIndex],
        manualRewrites: {
          ...(prev[resultIndex]?.manualRewrites || {}),
          [fixIndex]: value
        }
      }
    }));
  };

  const handleAcceptFix = (resultIndex, fixIndex, isSelected) => {
    // Freeform manual edit — read from ref first to avoid stale state when called
    // immediately after handleManualRewrite in the same render cycle.
    if (fixIndex === 'freeform') {
      const currentState = fixStates[resultIndex] || {};
      const freeformText = (
        latestFreeformRef.current[resultIndex] ||
        currentState.manualRewrites?.['freeform'] ||
        ''
      ).trim();
      if (!freeformText) return;
      const resultItem = results[resultIndex];
      if (!resultItem) return;
      const currentContent = resultItem.resumeText || '';
      const updatedContent = freeformText.length > 500
        ? freeformText
        : currentContent + '\n\n' + freeformText;
      const newResults = [...results];
      newResults[resultIndex] = { ...newResults[resultIndex], resumeText: updatedContent };
      setResults(newResults);
      return;
    }

    const resultItem = results[resultIndex];
    const fixPanel = resultItem.fixPanel || [];
    const fix = fixPanel[fixIndex];

    if (!fix) return;

    const currentState = fixStates[resultIndex] || { manualRewrites: {}, appliedFixes: new Set(), fixApplications: [] };
    const { manualRewrites, appliedFixes, fixApplications } = currentState;

    if (isSelected === false) {
      const newAppliedFixes = new Set(appliedFixes);
      newAppliedFixes.delete(fixIndex);

      const updatedApplications = fixApplications.filter(app => app.index !== fixIndex);

      let revertedContent = resultItem.originalResumeText || resultItem.resumeText;

      updatedApplications.forEach(app => {
        revertedContent = applyFixToContent(revertedContent, app.fix, app.fixText);
      });

      const newResults = [...results];
      newResults[resultIndex] = { ...newResults[resultIndex], resumeText: revertedContent };
      setResults(newResults);

      setFixStates(prev => ({
        ...prev,
        [resultIndex]: {
          ...prev[resultIndex],
          appliedFixes: newAppliedFixes,
          fixApplications: updatedApplications
        }
      }));
      return;
    }

    let fixText = manualRewrites[fixIndex]?.trim() || fix.suggestion?.trim() || '';

    if (!fixText && (fix.keyword || fix.original?.includes("Missing keyword"))) {
      fixText = fix.keyword || fix.original.replace("Missing keyword: ", "").trim();
    }
    if (!fixText && fix.original) {
      fixText = fix.original.trim();
    }

    if (!fixText) {
      alert('Please provide a manual rewrite or ensure the fix has a suggestion.');
      return;
    }

    const updatedContent = applyFixToContent(resultItem.resumeText, fix, fixText);

    if (updatedContent !== resultItem.resumeText) {
      const newResults = [...results];
      newResults[resultIndex] = { ...newResults[resultIndex], resumeText: updatedContent };
      setResults(newResults);

      const newAppliedFixes = new Set(appliedFixes);
      newAppliedFixes.add(fixIndex);

      setFixStates(prev => ({
        ...prev,
        [resultIndex]: {
          ...prev[resultIndex],
          appliedFixes: newAppliedFixes,
          fixApplications: [...fixApplications, { index: fixIndex, fix, fixText }]
        }
      }));
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      setFileName(file.name);

      const fileType = file.type;

      /* =====================
         TXT / MD
      ===================== */
      if (
        fileType === "text/plain" ||
        file.name.toLowerCase().endsWith(".md")
      ) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target.result || "";
          setResume(text);
          setResumePreloaded(false);
          saveResume(text, file.name, session?.access_token).catch(() => {});
        };
        reader.readAsText(file);
        return;
      }

      /* =====================
         PDF (ROBUST)
      ===================== */
      if (
        fileType === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf")
      ) {
        const arrayBuffer = await file.arrayBuffer();

        const pdf = await pdfjsLib.getDocument({
          data: arrayBuffer,
          disableFontFace: true,   // improves prod stability
          useSystemFonts: true
        }).promise;

        let fullText = "";

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const content = await page.getTextContent();

          const pageText = content.items
            .map(item => item.str)
            .join(" ");

          fullText += pageText + "\n\n";
        }

        if (!fullText.trim()) {
          throw new Error("PDF contains no readable text (scanned PDF).");
        }

        const pdfText = fullText.trim();
        setResume(pdfText);
        setResumePreloaded(false);
        saveResume(pdfText, file.name, session?.access_token).catch(() => {});
        return;
      }

      /* =====================
         DOCX / DOC
      ===================== */
      if (
        fileType ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        fileType === "application/msword" ||
        file.name.toLowerCase().endsWith(".docx")
      ) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });

        if (!result.value?.trim()) {
          throw new Error("DOCX file contains no readable text.");
        }

        const docxText = result.value.trim();
        setResume(docxText);
        setResumePreloaded(false);
        saveResume(docxText, file.name, session?.access_token).catch(() => {});
        return;
      }

      /* =====================
         UNSUPPORTED
      ===================== */
      throw new Error("Unsupported file format. Use TXT, PDF, or DOCX.");

    } catch (err) {
      console.error("❌ File upload error:", err);
      setError(err.message || "Failed to read file.");
      setFileName(null);
      setResume("");
    }
  };

  const handleOptimize = async () => {
    if (loading) return;

    const selectedDocuments = documents.filter((doc) => doc.selected);
    if (selectedDocuments.length === 0) {
      setError("Please select at least one document");
      return;
    }

    const docTypes = selectedDocuments.map((doc) => doc.type);

    saveResume(resume, fileName || 'manual-entry', session?.access_token).catch(() => {});
    setLoading(true);
    setResultsView(true);
    setError(null);
    setResults([]);
    setCreditRegranted(null);
    setGenerationFailed(false);
    setShowUpgrade(false);
    // Mark all selected docs as loading at once — 1 credit covers the whole run
    setLoadingDocs(Object.fromEntries(docTypes.map(t => [t, true])));

    const controller = new AbortController();
    streamAbortControllerRef.current = controller;

    // Set by a mid-stream {type:'error'} line — a whole-request failure the backend
    // can only report as a final NDJSON line once headers are already flushed, not
    // as a status code. Read from the STREAM_CUT_OFF catch branch below, since a
    // {type:'error'} line is always followed by the connection closing without a
    // summary line, which is exactly what triggers STREAM_CUT_OFF on the frontend.
    let streamErrorMessage = null;
    // Counts result lines (success or failure) received so far. A local counter, not
    // `results` state, because the catch branches below close over a stale `results`.
    let receivedResults = 0;

    try {
      // Score the original resume once before generation starts
      const originalScores = await scoreOriginalResume(resume, jobDescription, session?.access_token);
      originalScoresRef.current = originalScores;

      // Single request, streamed as NDJSON — one line per document as it finishes,
      // instead of one bulk response at the end. 1 credit consumed for the entire run.
      const summary = await generateDocument('batch', {
        content: resume,
        jobDescription,
        applicationInfo,
        docType: docTypes,
        tone,
        language,
      }, session?.access_token, {
        signal: controller.signal,
        onEvent: (evt) => {
          if (evt.type !== 'result') {
            if (evt.type === 'summary') setCreditRegranted(evt.creditRegranted === true);
            if (evt.type === 'error') {
              streamErrorMessage = evt.message;
              setCreditRegranted(evt.creditRegranted === true);
            }
            return;
          }

          receivedResults++;
          setLoadingDocs(prev => ({ ...prev, [evt.docType]: false }));

          if (evt.success && evt.optimizedContent) {
            const mapped = mapGeneratedResult(evt, originalScoresRef.current);
            setResults(prev => [...prev, mapped]);
            setResult(prev => prev || mapped);
            setGeneratedLanguage(language);
            // Auto-save this document now that it's arrived, rather than waiting for the batch
            autoSaveDocument(mapped, applicationInfo, session, setSaveStates);
          } else if (!evt.success) {
            // Failed doc — kept in `results` (not dropped) so the UI can show it as a
            // failed card instead of silently omitting it.
            setResults(prev => [...prev, { docType: evt.docType, failed: true, error: evt.error }]);
          }
          // evt.success && !evt.optimizedContent (empty model output, no error thrown)
          // is left out of `results` — same as today's silent-drop behavior.
        },
      });

      // The draft has served its purpose once at least one document was produced.
      // After a total failure keep it, so a refresh doesn't lose the user's inputs
      // before they try again.
      if (summary.successCount > 0) {
        clearLocalDraft(session?.user?.id);
      }

      // Refresh credit count now that one credit has been consumed
      fetchCredits(session?.access_token).then(data => {
        if (data !== null) {
          dispatch(setCredits(data.credits));
          dispatch(setIsAdmin(data.isAdmin));
        }
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        // Cancelled intentionally (new generation started, or navigated away) — nothing to show.
      } else if (err.code === 'NO_CREDITS') {
        setResultsView(false);
        setShowUpgrade(true);
      } else if (err.code === 'STREAM_CUT_OFF') {
        console.error("❌ Generation stream ended early:", streamErrorMessage || err.message);
        setError(streamErrorMessage || 'The connection was interrupted before generation finished. Please try again.');
        // Nothing arrived before the stream broke: the results view would be an empty
        // shell, so show the failed panel instead.
        if (receivedResults === 0) setGenerationFailed(true);
        // The credit was already consumed server-side before streaming began, so the
        // user's count is now stale regardless of how the stream ended.
        fetchCredits(session?.access_token).then(data => {
          if (data !== null) {
            dispatch(setCredits(data.credits));
            dispatch(setIsAdmin(data.isAdmin));
          }
        });
      } else {
        console.error("❌ Global Error:", err);
        setError(err.message);
        // A failure before anything streamed (e.g. the run could not be started) has
        // no results to show; go back to the form, where `error` is displayed.
        if (receivedResults === 0) setResultsView(false);
      }
    } finally {
      setLoadingDocs({});
      setLoading(false);
      if (streamAbortControllerRef.current === controller) {
        streamAbortControllerRef.current = null;
      }
    }
  };

  const [isExtracting, setIsExtracting] = useState(false);

  const handleToggle = (key, checked) => {
    setApplicationSource(prev => ({
      ...prev,
      [key]: { ...prev[key], selected: checked }
    }));
  };

  const handleTextChange = (key, text) => {
    setApplicationSource(prev => ({
      ...prev,
      [key]: { ...prev[key], text }
    }));
  };

  const handleSelectAll = (checked) => {
    setDocuments(docs => docs.map(doc => ({
      ...doc,
      selected: checked ? doc.type !== 'cv' : false,
    })));
  };

  const handleDocumentToggle = (index, checked) => {
    setDocuments(docs => {
      const newDocs = [...docs];
      newDocs[index] = { ...newDocs[index], selected: checked };
      if (checked) {
        const toggled = newDocs[index].type;
        const otherType = toggled === 'resume' ? 'cv' : toggled === 'cv' ? 'resume' : null;
        if (otherType !== null) {
          const otherIdx = newDocs.findIndex(d => d.type === otherType);
          if (otherIdx !== -1) newDocs[otherIdx] = { ...newDocs[otherIdx], selected: false };
        }
      }
      return newDocs;
    });
  };

  const handleStyleChange = (index, style) => {
    setDocuments(docs => {
      const newDocs = [...docs];
      newDocs[index].style = style;
      return newDocs;
    });
  };

  const handleAudienceChange = (index, audience) => {
    setDocuments(docs => {
      const newDocs = [...docs];
      newDocs[index].audience = audience;
      return newDocs;
    });
  };

  const allSelected = documents.filter(d => d.type !== 'cv').every(d => d.selected);

  const handleChange = (key, value) => {
    setApplicationInfo(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const extractJobInfo = async () => {
    if (!jobDescription || !jobDescription.trim()) {
      alert('Please enter a job description first');
      return;
    }

    setIsExtracting(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/resume/extract-job-info`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ jobDescription: jobDescription.trim() })
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to extract information');
      }

      const extractedInfo = result.data || {};

      setApplicationInfo(prev => ({
        ...prev,
        ...extractedInfo
      }));

    } catch (error) {
      console.error('Error extracting information:', error);
      alert(`Failed to extract information: ${error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };

  const shouldShowRefresh = jobDescription && jobDescription.trim().length > 0;

  const handleRefresh = () => {
    extractJobInfo();
  };

  const handleDownload = (format, resultItem) =>
    downloadResume(format, resultItem, applicationInfo, generatedLanguage, language, result);

  const handleLoadDemo = () => {
    setResume(DEMO_RESUME);
    setFileName(null);
    setApplicationInfo(prev => ({ ...prev, ...DEMO_APPLICATION_INFO }));
    setJobDescription(DEMO_JOB_DESCRIPTION);
    setApplicationSource(prev => ({
      ...prev,
      jobDescription: { selected: true, text: DEMO_JOB_DESCRIPTION },
    }));
    setDocuments(docs => docs.map(doc => ({
      ...doc,
      selected: doc.type === 'resume' || doc.type === 'coverLetter',
    })));
  };

  // "+New" — shared by the top nav, the mobile tab bar, and the results-view sidebar/tab bar.
  // Resets silently when there's nothing to lose; otherwise confirms first.
  const resetSetters = {
    setResume, setFileName, setResumePreloaded, setResumeSavedAt,
    setJobDescription, setApplicationInfo, setApplicationSource, setDocuments,
    setResultsView, setResults, setResult, setFixStates, setLoadingDocs,
    setGeneratedLanguage, setError, setSaveStates, setTone,
  };

  const resetAndClearDraft = () => {
    streamAbortControllerRef.current?.abort();
    resetApplicationState(resetSetters);
    clearLocalDraft(session?.user?.id);
    setShowDraftRestoredNotice(false);
  };

  const handleNewApplication = () => {
    const dirty = isApplicationDirty({ resume, fileName, jobDescription, applicationInfo, applicationSource, documents });
    if (dirty) {
      setShowNewAppConfirm(true);
    } else {
      resetAndClearDraft();
    }
  };

  const confirmNewApplication = () => {
    resetAndClearDraft();
    setShowNewAppConfirm(false);
  };

  const handleDiscardDraft = () => {
    resetAndClearDraft();
  };

  const cancelNewApplication = () => {
    setShowNewAppConfirm(false);
  };

  // "Try Again" on the failed panel: leave the results view but keep every form input
  // (unlike +New, which resets the application).
  const handleTryAgain = () => {
    setResultsView(false);
    setResults([]);
    setResult(null);
    setError(null);
    setLoadingDocs({});
    setCreditRegranted(null);
    setGenerationFailed(false);
  };

  // Status summary computation
  const selectedDocs = documents.filter((d) => d.selected);
  const hasResume = resume?.trim().length > 0 || !!fileName;
  const hasSourceInfo =
    (applicationSource.jobDescription.selected && jobDescription?.trim()) ||
    Object.entries(applicationSource).some(
      ([key, src]) => key !== 'jobDescription' && src.selected && src.text?.trim()
    );
  const requirementsMet = selectedDocs.length > 0 && hasResume && !!hasSourceInfo;

  const isGenerateDisabled =
    loading ||
    credits === 0 ||
    !documents.some((doc) => doc.selected) ||
    !resume?.trim() ||
    !hasSourceInfo;

  if (resultsView) {
    const saveEntries = Object.entries(saveStates);
    return (
      <>
        <ResultsPage
          results={results}
          documents={documents}
          fixStates={fixStates}
          loadingDocs={loadingDocs}
          onDownload={handleDownload}
          onAcceptFix={handleAcceptFix}
          onManualRewrite={handleManualRewrite}
          applicationInfo={applicationInfo}
          language={language}
          generatedLanguage={generatedLanguage}
          creditRegranted={creditRegranted}
          generationFailed={generationFailed}
          streamActive={loading}
          onTryAgain={handleTryAgain}
          onNewGeneration={() => {
            if (credits === 0) {
              const userId = session?.user?.id;
              if (userId) {
                sessionStorage.setItem(getDraftKey(userId), JSON.stringify({
                  userId,
                  resume,
                  jobDescription,
                  applicationInfo,
                  applicationSource,
                  documents,
                }));
              }
              navigate('/get-credits?from=generate');
            } else {
              handleNewApplication();
            }
          }}
        />
        {saveEntries.length > 0 && (
          <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9000,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'flex-end',
          }}>
            {saveEntries.map(([docType, state]) => (
              <div key={docType} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '12px',
                fontFamily: "'Syne', sans-serif",
                fontWeight: 600,
                letterSpacing: '0.04em',
                backdropFilter: 'blur(8px)',
                ...(state === 'saving' && {
                  background: 'rgba(15,22,41,0.92)',
                  border: '1px solid #1E2D4A',
                  color: '#6B7FA8',
                }),
                ...(state === 'saved' && {
                  background: 'rgba(0,212,160,0.1)',
                  border: '1px solid rgba(0,212,160,0.3)',
                  color: '#00D4A0',
                }),
                ...(state === 'error' && {
                  background: 'rgba(255,107,107,0.1)',
                  border: '1px solid rgba(255,107,107,0.3)',
                  color: '#ff6b6b',
                }),
              }}>
                {state === 'saving' && '⟳'}
                {state === 'saved'  && '✓'}
                {state === 'error'  && '✕'}
                <span>
                  {docType}
                  {state === 'saving' && ' — saving…'}
                  {state === 'saved'  && ' — saved'}
                  {state === 'error'  && ' — save failed'}
                </span>
              </div>
            ))}
          </div>
        )}
        {showNewAppConfirm && (
          <ConfirmModal
            title="Start a new application?"
            message="Your current work won't be saved."
            confirmLabel="Start new"
            cancelLabel="Cancel"
            onConfirm={confirmNewApplication}
            onCancel={cancelNewApplication}
          />
        )}
      </>
    );
  }

  const demoBtn = isAdmin ? (
    <button
      onClick={handleLoadDemo}
      style={{
        padding: '6px 18px',
        background: 'rgba(0, 229, 204, 0.08)',
        border: '1px solid rgba(0, 229, 204, 0.25)',
        borderRadius: '20px',
        color: '#00E5CC',
        fontSize: '11px',
        fontFamily: "'IBM Plex Sans', sans-serif",
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
      title="Pre-fill form with Sarah Chen demo data"
    >
      ✦ Try Demo
    </button>
  ) : null;

  return (
    <div className="page-bg">
      <AppNavBar rightSlot={demoBtn} onNewApplication={handleNewApplication} />
      <BottomTabBar onNewGeneration={handleNewApplication} />
      <div className="max-w-[1200px] mx-auto px-4 py-6">

        {showDraftRestoredNotice && (
          <div className="draft-restored-notice">
            <span>Restored your last draft.</span>
            <div className="draft-restored-notice-actions">
              <button type="button" className="draft-restored-notice-discard" onClick={handleDiscardDraft}>
                Discard
              </button>
              <button
                type="button"
                className="draft-restored-notice-dismiss"
                onClick={() => setShowDraftRestoredNotice(false)}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* ── Step 1 — Your Application ── */}
        <div className="step-section">
          <div className="step-header">
            <span className="step-badge">01</span>
            <span className="step-title">Your Application</span>
          </div>

          <div className="card mb-5">
            <ApplicationInfo
              applicationInfo={applicationInfo}
              isExtracting={isExtracting}
              shouldShowRefresh={shouldShowRefresh}
              onChange={handleChange}
              onRefresh={handleRefresh}
            />
          </div>

          <div className="card mb-5">
            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7FA8' }}>
                Application Source
              </span>
            </div>
            <ApplicationSource
              applicationSource={applicationSource}
              jobDescription={jobDescription}
              onToggle={handleToggle}
              onTextChange={handleTextChange}
              onJobDescriptionChange={setJobDescription}
            />
          </div>
        </div>

        {/* ── Step 2 — Your Resume ── */}
        <div className="step-section">
          <div className="step-header">
            <span className="step-badge">02</span>
            <span className="step-title">Your Resume</span>
          </div>

          <div className="card mb-5">
            <ResumeInput
              resume={resume}
              fileName={fileName}
              error={error}
              isPreloaded={resumePreloaded}
              savedAt={resumeSavedAt}
              savedResumes={savedResumes}
              onFileUpload={handleFileUpload}
              onRemoveFile={handleRemoveFile}
              onTextChange={handleResumeTextChange}
              onApplyResume={handleApplyResume}
              onSaveResume={handleSaveNamedResume}
            />
          </div>
        </div>

        {/* ── Step 3 — Choose Your Documents ── */}
        <div className="step-section">
          <div className="step-header">
            <span className="step-badge">03</span>
            <span className="step-title">Choose Your Documents</span>
            <div className="lang-inline">
              <span className="lang-label">Lang</span>
              <select
                className="lang-select"
                value={language}
                disabled={loading}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  setGeneratedLanguage(null);
                }}
              >
                <option value="English (US)">English (US)</option>
                <option value="English (UK)">English (UK)</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="German">German</option>
              </select>
            </div>
          </div>

          <div className="card mb-5">
            <DocumentSelector
              documents={documents}
              results={results}
              loading={loading}
              allSelected={allSelected}
              onSelectAll={handleSelectAll}
              onToggle={handleDocumentToggle}
              onStyleChange={handleStyleChange}
              onAudienceChange={handleAudienceChange}
              onDownload={handleDownload}
            />
          </div>
        </div>

        {/* ── CTA ── */}
        <div style={{ marginBottom: '24px' }}>
          <div className="status-summary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              {requirementsMet ? (
                <span className="status-ready">Ready to generate</span>
              ) : selectedDocs.length > 0 ? (
                <span className="status-highlight">
                  {selectedDocs.length} document{selectedDocs.length !== 1 ? 's' : ''} selected
                </span>
              ) : (
                'Select documents, add your resume, and enter job details above to generate.'
              )}
            </span>
            {credits !== null && (
              <span style={{
                fontSize: '11px',
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.06em',
                flexShrink: 0,
                marginLeft: '12px',
                color: credits === 0 ? '#ff6b6b' : '#00D4A0',
              }}>
                {credits} CREDIT{credits !== 1 ? 'S' : ''}
              </span>
            )}
          </div>

          {/* Stripe redirect confirmation banners */}
          {checkoutStatus === 'success' && (
            <div style={{
              margin: '12px 0',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(0,212,160,0.08)',
              border: '1px solid rgba(0,212,160,0.25)',
              color: '#00D4A0',
              fontSize: '13px',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              Payment successful — your credits have been added.
            </div>
          )}
          {checkoutStatus === 'cancelled' && (
            <div style={{
              margin: '12px 0',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(255,107,107,0.08)',
              border: '1px solid rgba(255,107,107,0.25)',
              color: '#ff6b6b',
              fontSize: '13px',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}>
              Checkout cancelled — no charge was made.
            </div>
          )}

          {/* Upgrade panel — shown when credits are confirmed 0 or after a server-side 402 */}
          {(showUpgrade || (credits !== null && credits === 0)) && (
            <div style={{
              margin: '16px 0',
              padding: '20px',
              borderRadius: '12px',
              background: '#0F1629',
              border: '1px solid #1E2D4A',
            }}>
              <div style={{
                fontSize: '11px',
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#6B7FA8',
                marginBottom: '8px',
              }}>
                Credits Required
              </div>
              <div style={{
                fontSize: '13px',
                color: '#8A9DC0',
                fontFamily: "'IBM Plex Sans', sans-serif",
                marginBottom: '16px',
                lineHeight: 1.5,
              }}>
                Each generation run uses 1 credit. Purchase a pack below to continue.
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {SKU_OPTIONS.map(opt => (
                  <button
                    key={opt.sku}
                    disabled={buyingCredits}
                    onClick={() => handleBuyCredits(opt.sku)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: '1px solid #2E6AFF',
                      background: 'transparent',
                      color: '#E8EEFF',
                      fontSize: '12px',
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      cursor: buyingCredits ? 'not-allowed' : 'pointer',
                      opacity: buyingCredits ? 0.5 : 1,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!buyingCredits) e.currentTarget.style.background = 'rgba(46,106,255,0.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {opt.label} — {opt.price}
                  </button>
                ))}
              </div>
            </div>
          )}

          <GenerateButton
            loading={loading}
            disabled={isGenerateDisabled}
            onClick={handleOptimize}
          />
        </div>

      </div>
      {showNewAppConfirm && (
        <ConfirmModal
          title="Start a new application?"
          message="Your current work won't be saved."
          confirmLabel="Start new"
          cancelLabel="Cancel"
          onConfirm={confirmNewApplication}
          onCancel={cancelNewApplication}
        />
      )}
    </div>
  );
};

export default Layout;
